/**
 * @fileoverview TokenAuthContext provides optimized token management.
 * Part of the Phase 4 authentication system optimization.
 *
 * This context handles token fetching, caching, and subscription management,
 * separated from user state for better performance.
 *
 * @example
 * ```tsx
 * const { getToken, subscribeToToken } = useTokenAuth()
 * ```
 */

import { useAuth0 } from '@auth0/auth0-react'
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { env } from '@busie/utils'
import {
  Audience,
  audienceUrls,
  BATCH_WINDOW,
  defaultAuthTokenConf,
  TOKEN_CONFIG,
} from '../constants'
export interface TokenSubscription {
  audience: Audience
  callback: (token: string) => void
}

export interface TokenAuthContextValue {
  getToken: (audience: Audience) => string
  subscribeToToken: (subscription: TokenSubscription) => void
  unsubscribeFromToken: (subscription: TokenSubscription) => void
  isBatchLoading: boolean
}

interface TokenCache {
  [audience: string]: {
    token: string
    expiresAt: number
    expiresIn: number | null
  }
}

interface PendingRequest {
  promise: Promise<string>
  timestamp: number
}

const TokenAuthContext = createContext<TokenAuthContextValue | undefined>(undefined)

/**
 * TokenAuthProvider component handles all token management operations.
 * This is optimized for the subscription pattern and separated from user state.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const TokenAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0()
  const [tokenCache, setTokenCache] = useState<TokenCache>({})
  const pendingRequests = useRef<{ [audience: string]: PendingRequest }>({})
  const subscriptions = useRef<Map<Audience, Set<TokenSubscription>>>(new Map())
  const batchTimeout = useRef<NodeJS.Timeout | null>(null)
  const pendingBatch = useRef<Set<Audience>>(new Set())
  const [batchLoading] = useState<boolean>(false)

  const calculateTokenExpiration = useCallback((expiresIn: number | null): number => {
    const now = Date.now()

    const isDevelopment = process.env.NODE_ENV === 'development'

    // ...

    const useExtendedLifetime = isDevelopment && env.VITE_DEV_EXTENDED_TOKENS === 'true'

    const tokenLifetimeMs = expiresIn
      ? Math.min(expiresIn * 1000, TOKEN_CONFIG.MAX_TOKEN_LIFETIME_MS)
      : useExtendedLifetime
        ? 24 * 60 * 60 * 1000
        : TOKEN_CONFIG.DEFAULT_TOKEN_LIFETIME_MS

    const expirationBufferMs = isDevelopment
      ? useExtendedLifetime
        ? 0
        : TOKEN_CONFIG.EXPIRATION_BUFFER_MS / 2
      : TOKEN_CONFIG.EXPIRATION_BUFFER_MS

    return now + tokenLifetimeMs - expirationBufferMs
  }, [])

  const fetchToken = useCallback(
    async (audience: Audience): Promise<string> => {
      const cacheAndNotifyToken = (token: string, expiresIn: number | null) => {
        const expiresAt = calculateTokenExpiration(expiresIn)

        setTokenCache((prev) => ({
          ...prev,
          [audience]: {
            token,
            expiresAt,
            expiresIn,
          },
        }))

        // Notify subscribers
        const audienceSubscriptions = subscriptions.current.get(audience)
        if (audienceSubscriptions) {
          audienceSubscriptions.forEach((subscription) => {
            subscription.callback(token)
          })
        }
      }

      try {
        console.log('🔑 Fetching token for audience:', audience, 'URL:', audienceUrls[audience])

        const token = await getAccessTokenSilently({
          ...defaultAuthTokenConf,
          audience: audienceUrls[audience],
        })

        let expiresIn: number | null = null
        try {
          const claims = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
          expiresIn = claims.exp ? claims.exp - Math.floor(Date.now() / 1000) : null
        } catch (e) {
          console.warn('Failed to parse token claims:', e)
        }

        console.log(
          '✅ Successfully fetched token for audience:',
          audience,
          'expires in:',
          expiresIn,
          'seconds'
        )
        cacheAndNotifyToken(token, expiresIn)
        return token
      } catch (error) {
        console.error('❌ Error fetching token for audience:', audience, error)

        // Check if this is a consent required error
        const errorMessage = (error as Error)?.message || ''
        const isConsentRequired =
          errorMessage.includes('Consent required') || errorMessage.includes('consent_required')

        if (isConsentRequired) {
          console.log('🔐 Consent required for audience:', audience, '- attempting popup login')
          try {
            const token = await getAccessTokenWithPopup({
              ...defaultAuthTokenConf,
              audience: audienceUrls[audience],
              scope: 'openid profile email',
            })

            let expiresIn: number | null = null
            try {
              const claims = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
              expiresIn = claims.exp ? claims.exp - Math.floor(Date.now() / 1000) : null
            } catch (e) {
              console.warn('Failed to parse token claims from popup:', e)
            }

            console.log(
              '✅ Successfully obtained consent and token for audience:',
              audience,
              'expires in:',
              expiresIn,
              'seconds'
            )
            cacheAndNotifyToken(token, expiresIn)
            return token
          } catch (popupError) {
            console.error('❌ Popup consent flow failed for audience:', audience, popupError)
            throw popupError
          }
        }

        throw error
      }
    },
    [getAccessTokenSilently, getAccessTokenWithPopup, calculateTokenExpiration]
  )

  const processBatch = useCallback(() => {
    const audiences = Array.from(pendingBatch.current)
    if (audiences.length === 0) return

    console.log('🔄 Processing token batch for audiences:', audiences)

    pendingBatch.current.clear()
    batchTimeout.current = null

    Promise.all(audiences.map(fetchToken)).catch(console.error)
  }, [fetchToken])

  const scheduleBatch = useCallback(
    (audience: Audience) => {
      pendingBatch.current.add(audience)

      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current)
      }

      batchTimeout.current = setTimeout(processBatch, BATCH_WINDOW)
    },
    [processBatch]
  )

  const getToken = useCallback(
    (audience: Audience): string => {
      const now = Date.now()
      // Check regular cache
      const cached = tokenCache[audience]
      if (cached && cached.expiresAt > now) {
        return cached.token
      }

      // Schedule fetch if needed
      if (!pendingRequests.current[audience]) {
        scheduleBatch(audience)
      }

      return cached?.token || ''
    },
    [tokenCache, scheduleBatch]
  )

  const subscribeToToken = useCallback(
    (subscription: TokenSubscription) => {
      const { audience } = subscription
      console.log('📡 Subscribing to audience:', audience)

      if (!subscriptions.current.has(audience)) {
        subscriptions.current.set(audience, new Set())
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subscriptions.current.get(audience)!.add(subscription)

      // Log current audiences being tracked
      const trackedAudiences = Array.from(subscriptions.current.keys())
      console.log('📊 Currently tracking audiences:', trackedAudiences)

      // Immediately provide cached token if available
      const cached = tokenCache[audience]
      if (cached && cached.expiresAt > Date.now()) {
        console.log('💾 Using cached token for audience:', audience)
        subscription.callback(cached.token)
      } else {
        console.log('🔍 No valid cached token for audience:', audience, '- scheduling fetch')
        // Schedule fetch for missing token
        scheduleBatch(audience)
      }
    },
    [tokenCache, scheduleBatch]
  )

  const unsubscribeFromToken = useCallback((subscription: TokenSubscription) => {
    const { audience } = subscription
    const audienceSubscriptions = subscriptions.current.get(audience)
    if (audienceSubscriptions) {
      audienceSubscriptions.delete(subscription)
      if (audienceSubscriptions.size === 0) {
        subscriptions.current.delete(audience)
      }
    }
  }, [])

  const value = useMemo<TokenAuthContextValue>(
    () => ({
      getToken,
      subscribeToToken,
      unsubscribeFromToken,
      isBatchLoading: batchLoading,
    }),
    [getToken, subscribeToToken, unsubscribeFromToken, batchLoading]
  )

  return <TokenAuthContext.Provider value={value}>{children}</TokenAuthContext.Provider>
}

const StubTokenAuthContext = createContext<TokenAuthContextValue | undefined>(undefined)

/**
 * Stub TokenAuthProvider for embedded/anonymous apps
 * Provides empty implementations to prevent context errors
 */
export const StubTokenAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useMemo<TokenAuthContextValue>(
    () => ({
      getToken: () => '',
      subscribeToToken: () => {
        return () => {
          // No-op
        }
      },
      unsubscribeFromToken: () => {
        return () => {
          // No-op
        }
      },
      isBatchLoading: false, // Never loading
    }),
    []
  )

  return <StubTokenAuthContext.Provider value={value}>{children}</StubTokenAuthContext.Provider>
}

/**
 * Hook for accessing token authentication context
 * @returns {TokenAuthContextValue} Token management functions
 * @throws {Error} When used outside of TokenAuthProvider
 */

interface UseTokenAuthProps {
  isEmbedded?: boolean
}
export const useTokenAuth = ({ isEmbedded = false }: UseTokenAuthProps): TokenAuthContextValue => {
  const context = useContext(isEmbedded ? StubTokenAuthContext : TokenAuthContext)
  if (!context) {
    throw new Error('useTokenAuth must be used within a TokenAuthProvider')
  }
  return context
}
